import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const STATS = [
  { value: 18400, label: "Members Worldwide", suffix: "+" },
  { value: 36, label: "States Reached", suffix: "" },
  { value: 4.5, label: "Billion ₦ Distributed", suffix: "B+" },
  { value: 27, label: "Projects Funded", suffix: "" },
];

const FOCUS_AREAS = [
  { title: "Education", desc: "Bridging access gaps and empowering learners through targeted educational grants." },
  { title: "Youth Empowerment", desc: "Equipping the next generation with skills, mentorship, and opportunity." },
  { title: "Community Development", desc: "Building resilient communities through infrastructure and social programs." },
  { title: "Poverty Alleviation", desc: "Creating sustainable pathways out of poverty through grassroots funding." },
  { title: "Agriculture", desc: "Supporting climate-smart farming and food security across rural Nigeria." },
  { title: "Business Development", desc: "Empowering entrepreneurs and cooperatives with seed funding and training." },
];

const TESTIMONIALS = [
  { quote: "The Revolutionary Foundation changed the trajectory of our school feeding program. Within 3 months, 6,000 girls had guaranteed educational support.", name: "Aisha Bello", org: "Hope Bridge Initiative, Kano", avatar: "AB" },
  { quote: "Their grant process is transparent, fair, and genuinely focused on impact. We felt supported throughout the entire journey from application to disbursement.", name: "Emmanuel Okafor", org: "Youth Forge NGO, Lagos", avatar: "EO" },
  { quote: "Revolutionary Foundation doesn't just fund — they partner. Their team's guidance helped us scale our clean water project to 1,800 residents.", name: "Ngozi Adeyemi", org: "Wetlands Watch NGO, Bayelsa", avatar: "NA" },
  { quote: "As a widows cooperative, we never thought we'd access institutional funding. Revolutionary Foundation made it possible. 180 women now have sustainable income.", name: "Fatima Musa", org: "Women Rising Collective, Plateau", avatar: "FM" },
  { quote: "The climate-smart farming grant helped 780 farmers in Sokoto adapt to drought conditions. Our harvest improved by 60% in the first season.", name: "Ibrahim Aliyu", org: "Sahelian Farmers Alliance, Sokoto", avatar: "IA" },
];

function useCountUp(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isFloat = !Number.isInteger(target);
    const steps = 60;
    const increment = target / steps;
    let current = 0, step = 0;
    const timer = setInterval(() => {
      step++; current += increment;
      if (step >= steps) { setCount(target); clearInterval(timer); }
      else setCount(isFloat ? parseFloat(current.toFixed(1)) : Math.floor(current));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [start, target, duration]);
  return count;
}

function StatCard({ value, label, suffix, animate }) {
  const count = useCountUp(value, 1800, animate);
  return (
    <div style={{ textAlign: "center", padding: "36px 20px", borderRight: "1px solid rgba(255,255,255,0.08)", position: "relative" }}>
      <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 40, height: 2, background: "linear-gradient(90deg, transparent, #C9963E, transparent)" }} />
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 48, fontWeight: 900, color: "#E8B96A", lineHeight: 1 }}>{count}{suffix}</div>
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 8, letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}

export default function HomePage() {
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsVisible(true); }, { threshold: 0.3 });
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <style>{`
        .hero {
          min-height: 100vh;
          background: linear-gradient(160deg, #071428 0%, #0B1F3A 55%, #122844 100%);
          display: flex; align-items: center; justify-content: center;
          text-align: center; padding: 120px 5vw 80px;
          position: relative; overflow: hidden;
        }
        .hero::before {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(ellipse 80% 60% at 50% 30%, rgba(201,150,62,0.07) 0%, transparent 70%);
        }
        .hero-grid {
          position: absolute; inset: 0; opacity: 0.03;
          background-image:
            repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(201,150,62,1) 40px, rgba(201,150,62,1) 41px),
            repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(201,150,62,1) 40px, rgba(201,150,62,1) 41px);
        }
        .hero-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(201,150,62,0.1); border: 1px solid rgba(201,150,62,0.25);
          padding: 6px 16px; border-radius: 50px; margin-bottom: 28px;
        }
        .pulse-dot { width: 7px; height: 7px; border-radius: 50%; background: #C9963E; animation: pulse 2s infinite; }
        .hero-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(44px, 7.5vw, 90px); font-weight: 900;
          color: #fff; line-height: 1.06; margin-bottom: 22px;
        }
        .hero-title em { color: #E8B96A; font-style: italic; }
        .hero-sub { font-size: clamp(16px, 2vw, 19px); color: rgba(255,255,255,0.6); max-width: 600px; margin: 0 auto 40px; line-height: 1.75; font-weight: 300; }
        .hero-btns { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }
        .stats-bar { background: #0B1F3A; }
        .stats-inner { display: grid; grid-template-columns: repeat(4,1fr); max-width: 1100px; margin: 0 auto; }
        .mission-wrap { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; max-width: 1200px; margin: 0 auto; }
        .mission-text p { color: var(--text-muted); font-size: 16px; line-height: 1.85; margin-bottom: 18px; }
        .mission-nums { display: flex; gap: 32px; margin-top: 32px; flex-wrap: wrap; }
        .focus-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 24px; max-width: 1200px; margin: 48px auto 0; }
        .focus-card {
          padding: 32px 26px; border: 1px solid var(--border-light);
          position: relative; overflow: hidden; background: white; transition: all 0.3s;
        }
        .focus-card::after {
          content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 3px;
          background: linear-gradient(90deg, #C9963E, #E8B96A);
          transform: scaleX(0); transition: 0.3s; transform-origin: left;
        }
        .focus-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); }
        .focus-card:hover::after { transform: scaleX(1); }

        /* TESTIMONIALS SLIDER */
        .t-section {
          background: linear-gradient(160deg, #071428, #0B1F3A);
          padding: 90px 0; position: relative; overflow: hidden;
        }
        .t-track-wrap { overflow: hidden; }
        .t-track {
          display: flex; gap: 28px;
          animation: slideLeft 30s linear infinite;
          width: max-content;
        }
        .t-track:hover { animation-play-state: paused; }
        @keyframes slideLeft {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .t-card {
          width: 420px; flex-shrink: 0;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          padding: 32px; border-radius: 4px;
        }
        .t-quote-mark {
          font-family: 'Playfair Display', serif;
          font-size: 60px; color: rgba(201,150,62,0.3);
          line-height: 1; margin-bottom: -10px;
        }
        .t-quote { font-size: 15px; color: rgba(255,255,255,0.8); line-height: 1.75; margin-bottom: 24px; font-style: italic; }
        .t-avatar { width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(135deg, #C9963E, #E8B96A); display: flex; align-items: center; justify-content: center; font-weight: 700; color: #0B1F3A; font-size: 15px; flex-shrink: 0; }
        .cta-box {
          max-width: 820px; margin: 0 auto;
          background: linear-gradient(135deg, #0B1F3A, #1a3a5c);
          padding: 72px 60px; border-radius: 4px; text-align: center; position: relative; overflow: hidden;
        }
        .cta-box::before {
          content: ''; position: absolute; top: -50%; right: -20%;
          width: 400px; height: 400px; border-radius: 50%;
          background: radial-gradient(circle, rgba(201,150,62,0.1), transparent 70%); pointer-events: none;
        }
        .cta-box p { color: rgba(255,255,255,0.55); font-size: 17px; margin-bottom: 36px; line-height: 1.7; }
        @media (max-width: 900px) {
          .stats-inner { grid-template-columns: repeat(2,1fr); }
          .mission-wrap { grid-template-columns: 1fr; }
          .focus-grid { grid-template-columns: repeat(2,1fr); }
          .t-card { width: 320px; }
        }
        @media (max-width: 540px) {
          .focus-grid { grid-template-columns: 1fr; }
          .cta-box { padding: 48px 24px; }
        }
      `}</style>

      {/* HERO */}
      <section className="hero">
        <div className="hero-grid" />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="hero-badge fade-up">
            <div className="pulse-dot" />
            <span style={{ fontSize: 12, color: "#E8B96A", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase" }}>
              Empowering Communities Across Nigeria
            </span>
          </div>
          <h1 className="hero-title fade-up-d1">
            Funding the<br /><em>Change</em> You<br />Want to See
          </h1>
          <p className="hero-sub fade-up-d2">
            Revolutionary Foundation partners with NGOs, community leaders, and social innovators to distribute grants that transform lives and build lasting impact across all 36 states.
          </p>
          <div className="hero-btns fade-up-d3">
            <Link to="/apply" className="btn-primary">Apply for a Grant →</Link>
            <Link to="/membership" className="btn-outline">Become a Member</Link>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="stats-bar" ref={statsRef}>
        <div className="stats-inner">
          {STATS.map(s => <StatCard key={s.label} {...s} animate={statsVisible} />)}
        </div>
      </section>

      {/* MISSION */}
      <section style={{ padding: "100px 5vw", background: "var(--off-white)" }}>
        <div className="mission-wrap">
          <div className="mission-text">
            <span className="section-eyebrow">Our Mission</span>
            <h2 className="section-title">Building Bridges Between Resources and Need</h2>
            <p>
              Founded in 2019, Revolutionary Foundation was established with a single conviction: that every community deserves access to the resources it needs to thrive.
            </p>
            <p>
              We began with 42 founding members and a ₦1.5 million seed fund. By 2023, we had surpassed ₦2 Billion in distributions and expanded to all 36 states. Today, we have mobilized over ₦4.5 Billion in grants supporting 27 projects and 18,400+ members.
            </p>
            <div className="mission-nums">
              {[["2019", "Year Founded"], ["₦4.5B+", "Disbursed"], ["All 36", "States Reached"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, fontWeight: 700, color: "var(--navy)" }}>{v}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {[
              ["Over ₦4.5B Distributed", "Since 2019, we have disbursed over 4.5 Billion Naira to communities across Nigeria."],
              ["18,400+ Active Members", "Our growing community of changemakers spans all 36 states and the FCT."],
              ["27 Projects Funded", "From education to agriculture, every funded project creates measurable community impact."],
              ["International Partnerships", "We act as an intermediary between international donors and local Nigerian communities."],
            ].map(([title, desc]) => (
              <div key={title} style={{ display: "flex", gap: 16, padding: "20px", background: "white", border: "1px solid var(--border-light)" }}>
                <div>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>{title}</div>
                  <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOCUS AREAS */}
      <section style={{ padding: "80px 5vw", background: "white" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center" }}>
            <span className="section-eyebrow">What We Support</span>
            <h2 className="section-title">Our Focus Areas</h2>
          </div>
          <div className="focus-grid">
            {FOCUS_AREAS.map(f => (
              <div className="focus-card" key={f.title}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 19, color: "var(--navy)", marginBottom: 10, fontWeight: 700 }}>{f.title}</div>
                <div style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.7 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — AUTO SLIDING */}
      <section className="t-section">
        <div style={{ maxWidth: 1200, margin: "0 auto", textAlign: "center", padding: "0 5vw", marginBottom: 48 }}>
          <span className="section-eyebrow" style={{ color: "#E8B96A" }}>Voices of Impact</span>
          <h2 className="section-title light">What Our Partners Say</h2>
          <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 14 }}>Hover over any card to pause</p>
        </div>
        <div className="t-track-wrap">
          {/* Duplicate testimonials for seamless loop */}
          <div className="t-track">
            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
              <div className="t-card" key={i}>
                <div className="t-quote-mark">"</div>
                <p className="t-quote">{t.quote}</p>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="t-avatar">{t.avatar}</div>
                  <div>
                    <div style={{ fontWeight: 600, color: "white", fontSize: 14 }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: "#E8B96A", marginTop: 2 }}>{t.org}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "100px 5vw", background: "var(--off-white)" }}>
        <div className="cta-box">
          <span className="section-eyebrow" style={{ color: "#E8B96A" }}>Get Started Today</span>
          <h2 className="section-title light">Ready to Make an Impact?</h2>
          <p>Join 18,400+ changemakers. Apply for a grant or become a member and help fund the next generation of community leaders across Nigeria.</p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginBottom: 20 }}>
            <Link to="/apply" className="btn-primary">Apply for a Grant →</Link>
            <Link to="/membership" className="btn-outline">Become a Member — ₦250</Link>
          </div>
          <Link to="/terms" style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", display: "block", marginTop: 12 }}>
            By joining, you agree to our Terms & Conditions
          </Link>
        </div>
      </section>
    </>
  );
}
